import { guardianKeys } from '@/features/guardian/queries/query-keys';
import { playerGuardiansKeys } from '@/queries/use-player-guardians-query';
import {
  removePlayerGuardian,
  type RemovePlayerGuardianPayload,
} from '@/services/remove-player-guardian';
import { useSessionStore } from '@/stores/session-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type RemoveCoGuardianVariables = {
  /** The co-guardian being removed from the player's circle. */
  guardianUid: string;
} & RemovePlayerGuardianPayload;

/**
 * Primary guardian removing a co-guardian from a player's circle via
 * DELETE `/api/v1/guardians/me/players/{playerId}/guardians/{guardianUid}` with a
 * `{ reason, detail }` body. The co-guardian loses access immediately and is notified by
 * email (server-side). Invalidates the caller's players list so the circle refreshes.
 *
 * Distinct from `useRemovePlayerGuardianMutation` (self-leave, no body) so that flow stays
 * untouched.
 */
export function useRemoveCoGuardianMutation(playerId: string) {
  const queryClient = useQueryClient();
  const uid = useSessionStore(s => s.user?.uid);
  return useMutation({
    mutationFn: ({ guardianUid, reason, detail }: RemoveCoGuardianVariables) =>
      removePlayerGuardian(playerId, guardianUid, { reason, detail }),
    onSuccess: async () => {
      // Mark the caller's players list stale (a removed member can change its summary).
      queryClient.invalidateQueries({ queryKey: guardianKeys.players(uid) });
      // Actively refetch the player's circle so the screen we return to shows fresh data
      // rather than the cached list that still includes the removed co-guardian.
      await queryClient.refetchQueries({ queryKey: playerGuardiansKeys.list(playerId) });
    },
  });
}
